import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  Plus,
  AlertTriangle,
  Search,
  Package,
  Printer,
  ExternalLink,
  MoreVertical,
  Boxes,
  Copy,
  FileEdit,
  Trash2,
  X,
} from 'lucide-react';
import { productsApi } from '@/api/productsApi';
import { productDraftsApi } from '@/api/productDraftsApi';
import { itemTypesApi } from '@/api/itemTypesApi';
import { collectionsApi } from '@/api/collectionsApi';
import { productionMethodsApi } from '@/api/productionMethodsApi';
import { formatCurrency } from '@/utils/formatters';
import { useToast } from '@/hooks/useToast';
import { ProductFormShell } from '@/components/products/ProductForm/ProductFormShell';
import { EntityLink } from '@/components/detail/EntityLink';
import type { Product } from '@/types/api';

type ExtendedProduct = Product & {
  itemTypeId?: string | null;
  collectionId?: string | null;
  productionMethodIds?: string[];
  productDesign?: string | null;
};

function missingProductFields(product: Product): string[] {
  const missing: string[] = [];
  if (!product.category && !product.categories?.length) missing.push('category');
  if (!product.basePrice || product.basePrice <= 0) missing.push('price');
  if (!product.cost || product.cost <= 0) missing.push('cost');
  if (product.tracksInventory && (!product.variants || product.variants.length === 0)) missing.push('variant');
  if (!product.primaryImageUrl && (!product.imageUrls || product.imageUrls.length === 0)) missing.push('photo');
  if (!product.productionMethodIds?.length && !product.productType) missing.push('production');
  return missing;
}

export function Products() {
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [filter3dPrinted, setFilter3dPrinted] = useState<boolean | null>(null);
  const [filterItemTypeId, setFilterItemTypeId] = useState<string>('');
  const [filterCollectionId, setFilterCollectionId] = useState<string>('');
  const [filterProductionMethodId, setFilterProductionMethodId] = useState<string>('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [duplicateProduct, setDuplicateProduct] = useState<Product | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'duplicate'>('create');
  const [activeDraftId, setActiveDraftId] = useState<string | null>(null);
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { success, error } = useToast();

  const { data: products, isLoading } = useQuery({
    queryKey: ['products'],
    queryFn: productsApi.getAll,
  });

  const { data: drafts } = useQuery({
    queryKey: ['product-drafts'],
    queryFn: productDraftsApi.getMine,
  });

  const { data: itemTypes } = useQuery({
    queryKey: ['item-types'],
    queryFn: () => itemTypesApi.getAll(false),
  });

  const { data: collections } = useQuery({
    queryKey: ['collections'],
    queryFn: () => collectionsApi.getAll(false),
  });

  const { data: productionMethods } = useQuery({
    queryKey: ['production-methods'],
    queryFn: () => productionMethodsApi.getAll(false),
  });

  const openDrafts = drafts?.filter((d) => !d.publishedProductId) ?? [];

  useEffect(() => {
    const collection = searchParams.get('collection');
    if (collection) {
      setFilterCollectionId(collection);
    }
  }, [searchParams]);

  const filteredProducts = products?.filter((product) => {
    const extended = product as ExtendedProduct;

    if (filter3dPrinted !== null && product.is3dPrinted !== filter3dPrinted) {
      return false;
    }

    if (filterItemTypeId) {
      if (extended.itemTypeId) {
        if (extended.itemTypeId !== filterItemTypeId) return false;
      } else {
        return false;
      }
    }

    if (filterCollectionId) {
      if (extended.collectionId) {
        if (extended.collectionId !== filterCollectionId) return false;
      } else {
        return false;
      }
    }

    if (filterProductionMethodId) {
      const method = productionMethods?.find((m) => m.id === filterProductionMethodId);
      if (extended.productionMethodIds?.length) {
        if (!extended.productionMethodIds.includes(filterProductionMethodId)) return false;
      } else if (method) {
        if (product.productType !== method.code) return false;
      } else {
        return false;
      }
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        product.name.toLowerCase().includes(query) ||
        product.sku.toLowerCase().includes(query) ||
        product.category?.toLowerCase().includes(query) ||
        extended.productDesign?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const totalProducts = products?.length || 0;
  const total3dPrinted = products?.filter((p) => p.is3dPrinted).length || 0;
  const totalVariants = products?.reduce((sum, p) => sum + (p.variants?.length || 0), 0) || 0;
  const productsMissingData = products?.filter((product) => missingProductFields(product).length > 0) ?? [];

  const handleSuccess = () => {
    success(
      editingProduct ? 'Product Updated' : 'Product Created',
      editingProduct
        ? 'Product has been updated successfully.'
        : 'Your new product has been added to the inventory system.',
      5000
    );
    setEditingProduct(null);
    setActiveDraftId(null);
  };

  const handleEdit = (product: Product) => {
    setEditingProduct(product);
    setDuplicateProduct(null);
    setActiveDraftId(null);
    setModalMode('edit');
    setShowAddModal(true);
    setActiveMenu(null);
  };

  const handleDuplicate = (product: Product) => {
    setDuplicateProduct(product);
    setEditingProduct(null);
    setActiveDraftId(null);
    setModalMode('duplicate');
    setShowAddModal(true);
    setActiveMenu(null);
  };

  const handleDelete = async (product: Product) => {
    setActiveMenu(null);
    if (!window.confirm(`Delete product "${product.name}"? This will remove it from active product lists.`)) {
      return;
    }
    try {
      await productsApi.delete(product.id);
      await queryClient.invalidateQueries({ queryKey: ['products'] });
      success('Product Deleted', `${product.name} was removed.`, 5000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Product could not be deleted.';
      error('Delete Failed', message, 8000);
    }
  };

  const handleResumeDraft = (draftId: string) => {
    setEditingProduct(null);
    setDuplicateProduct(null);
    setActiveDraftId(draftId);
    setModalMode('create');
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setEditingProduct(null);
    setDuplicateProduct(null);
    setActiveDraftId(null);
    setModalMode('create');
  };

  const hasActiveFilters =
    filterItemTypeId || filterCollectionId || filterProductionMethodId;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-gray-900 dark:text-warm-cream">Products</h1>
          <p className="text-gray-600 dark:text-warm-sand mt-1">
            Manage your products and 3D printed variants
          </p>
        </div>
        <button
          onClick={() => {
            setModalMode('create');
            setActiveDraftId(null);
            setShowAddModal(true);
          }}
          className="btn-primary"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add Product
        </button>
      </div>

      {openDrafts.length > 0 && (
        <div className="card p-4 border-amber-450/30 bg-amber-450/5">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div>
              <h2 className="font-medium text-gray-900 dark:text-warm-cream flex items-center gap-2">
                <FileEdit className="w-4 h-4 text-amber-450" />
                Unpublished Drafts
              </h2>
              <p className="text-sm text-gray-600 dark:text-warm-sand mt-1">
                You have {openDrafts.length} saved draft{openDrafts.length !== 1 ? 's' : ''} in progress.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            {openDrafts.map((draft) => (
              <button
                key={draft.id}
                onClick={() => handleResumeDraft(draft.id)}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white dark:bg-charcoal-800 border border-amber-450/30 text-sm text-gray-900 dark:text-warm-cream hover:border-amber-450 transition-colors"
              >
                <span className="font-medium truncate max-w-[200px]">
                  {draft.label || draft.payload.name || 'Untitled draft'}
                </span>
                <span className="text-xs text-gray-500 dark:text-warm-sand/60">
                  {new Date(draft.updatedAt).toLocaleDateString()}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {productsMissingData.length > 0 && (
        <div className="card p-4 border-blue-500/30 bg-blue-500/5">
          <h2 className="font-medium text-gray-900 dark:text-warm-cream flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-blue-500" />
            Products Missing Data
          </h2>
          <p className="text-sm text-gray-600 dark:text-warm-sand mt-1">
            {productsMissingData.length} product{productsMissingData.length !== 1 ? 's have' : ' has'} missing catalog or inventory details.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-450/10 flex items-center justify-center">
              <Package className="w-5 h-5 text-amber-450" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-warm-sand">Total Products</p>
              <p className="font-display text-2xl font-bold text-gray-900 dark:text-warm-cream">{totalProducts}</p>
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
              <Boxes className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-warm-sand">Total Variants</p>
              <p className="font-display text-2xl font-bold text-gray-900 dark:text-warm-cream">{totalVariants}</p>
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
              <Printer className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-warm-sand">3D Printed</p>
              <p className="font-display text-2xl font-bold text-gray-900 dark:text-warm-cream">{total3dPrinted}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="card p-4 space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-warm-taupe" />
            <input
              type="text"
              placeholder="Search products by name, SKU, or category..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-xl text-gray-900 dark:text-warm-cream placeholder-gray-400 dark:placeholder-charcoal-500 focus:outline-none focus:border-amber-450 transition-all"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setFilter3dPrinted(filter3dPrinted === true ? null : true)}
              className={`
                px-4 py-2 rounded-xl font-medium transition-all border
                ${
                  filter3dPrinted === true
                    ? 'bg-purple-500/20 text-purple-600 dark:text-purple-400 border-purple-500/50'
                    : 'bg-gray-100 dark:bg-charcoal-700 text-gray-600 dark:text-warm-sand border-gray-300 dark:border-charcoal-600 hover:border-charcoal-500'
                }
              `}
            >
              <Printer className="w-4 h-4 mr-2 inline" />
              3D Printed
            </button>
            <button
              onClick={() => setFilter3dPrinted(filter3dPrinted === false ? null : false)}
              className={`
                px-4 py-2 rounded-xl font-medium transition-all border
                ${
                  filter3dPrinted === false
                    ? 'bg-amber-450/20 text-amber-450 border-amber-450/50'
                    : 'bg-gray-100 dark:bg-charcoal-700 text-gray-600 dark:text-warm-sand border-gray-300 dark:border-charcoal-600 hover:border-charcoal-500'
                }
              `}
            >
              <Package className="w-4 h-4 mr-2 inline" />
              Standard
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <select
            value={filterItemTypeId}
            onChange={(e) => setFilterItemTypeId(e.target.value)}
            className="px-3 py-2.5 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-xl text-sm text-gray-900 dark:text-warm-cream"
          >
            <option value="">All item types</option>
            {itemTypes?.map((item) => (
              <option key={item.id} value={item.id}>
                {item.icon ? `${item.icon} ` : ''}{item.name}
              </option>
            ))}
          </select>

          <select
            value={filterCollectionId}
            onChange={(e) => setFilterCollectionId(e.target.value)}
            className="px-3 py-2.5 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-xl text-sm text-gray-900 dark:text-warm-cream"
          >
            <option value="">All collections</option>
            {collections?.map((collection) => (
              <option key={collection.id} value={collection.id}>
                {collection.icon ? `${collection.icon} ` : ''}{collection.name}
              </option>
            ))}
          </select>

          <select
            value={filterProductionMethodId}
            onChange={(e) => setFilterProductionMethodId(e.target.value)}
            className="px-3 py-2.5 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-xl text-sm text-gray-900 dark:text-warm-cream"
          >
            <option value="">All production methods</option>
            {productionMethods?.map((method) => (
              <option key={method.id} value={method.id}>
                {method.icon ? `${method.icon} ` : ''}{method.name}
              </option>
            ))}
          </select>
        </div>

        {hasActiveFilters && (
          <button
            type="button"
            onClick={() => {
              setFilterItemTypeId('');
              setFilterCollectionId('');
              setFilterProductionMethodId('');
            }}
            className="inline-flex items-center gap-1 text-sm text-amber-450 hover:text-amber-500"
          >
            <X className="w-4 h-4" />
            Clear taxonomy filters
          </button>
        )}
      </div>

      <div className="table-container">
        <table className="table">
          <thead>
            <tr>
              <th>Product</th>
              <th>SKU</th>
              <th>Category</th>
              <th>Type</th>
              <th>Price</th>
              <th>Cost</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="text-center py-12 text-gray-600 dark:text-warm-sand">
                  <div className="flex items-center justify-center gap-3">
                    <div className="w-5 h-5 border-2 border-amber-450/30 border-t-amber-450 rounded-full animate-spin" />
                    Loading products...
                  </div>
                </td>
              </tr>
            ) : filteredProducts?.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-12">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-charcoal-700 flex items-center justify-center">
                      <Package className="w-8 h-8 text-gray-600 dark:text-warm-sand" />
                    </div>
                    <div>
                      <p className="text-gray-900 dark:text-warm-cream font-medium">No products found</p>
                      <p className="text-gray-600 dark:text-warm-sand text-sm mt-1">
                        {searchQuery || hasActiveFilters
                          ? 'Try adjusting your search or filters'
                          : 'Add your first product to get started'}
                      </p>
                    </div>
                    {!searchQuery && !hasActiveFilters && (
                      <button onClick={() => setShowAddModal(true)} className="btn-primary mt-2">
                        <Plus className="w-4 h-4 mr-2" />
                        Add Product
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              filteredProducts?.map((product) => (
                <tr key={product.id}>
                  <td>
                    <div className="flex items-center gap-3">
                      <div
                        className={`
                        w-10 h-10 rounded-xl flex items-center justify-center
                        ${product.is3dPrinted ? 'bg-purple-500/10' : 'bg-gray-100 dark:bg-charcoal-700'}
                      `}
                      >
                        {product.is3dPrinted ? (
                          <Printer className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                        ) : (
                          <Package className="w-5 h-5 text-gray-600 dark:text-warm-sand" />
                        )}
                      </div>
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <EntityLink type="product" id={product.id} label={product.name} />
                          {missingProductFields(product).length > 0 && (
                            <span
                              className="inline-flex items-center gap-1 rounded-full bg-blue-500/10 px-2 py-0.5 text-[11px] font-medium text-blue-700 dark:text-blue-300"
                              title={`Missing: ${missingProductFields(product).join(', ')}`}
                            >
                              <AlertTriangle className="w-3 h-3" />
                              Missing Data
                            </span>
                          )}
                        </div>
                        {product.variants && product.variants.length > 0 && (
                          <p className="text-xs text-gray-600 dark:text-warm-sand/70 mt-0.5">
                            {product.variants.length === 1
                              ? product.variants[0].name || product.variants[0].variantSku || 'Standard'
                              : `${product.variants.length} styles: ${product.variants
                                  .slice(0, 2)
                                  .map((v) => v.name || v.variantSku || 'Style')
                                  .join(', ')}${product.variants.length > 2 ? '…' : ''}`}
                          </p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="mono text-gray-600 dark:text-warm-sand text-sm">{product.sku}</td>
                  <td>
                    {product.category ? (
                      <span className="badge-info">{product.category}</span>
                    ) : (
                      <span className="text-gray-600 dark:text-warm-sand/50">-</span>
                    )}
                  </td>
                  <td>
                    {product.is3dPrinted ? (
                      <span className="badge-warning">
                        <Printer className="w-3 h-3 mr-1 inline" />
                        3D Printed
                      </span>
                    ) : (
                      <span className="badge-neutral">Standard</span>
                    )}
                  </td>
                  <td className="font-medium text-gray-900 dark:text-warm-cream">
                    {formatCurrency(product.basePrice)}
                  </td>
                  <td className="text-gray-600 dark:text-warm-sand">
                    {product.cost > 0 ? formatCurrency(product.cost) : '-'}
                  </td>
                  <td className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {product.wordpressProductId && (
                        <button
                          className="p-2 hover:bg-gray-100 dark:hover:bg-charcoal-700 rounded-lg transition-colors"
                          title="View on WordPress"
                        >
                          <ExternalLink className="w-4 h-4 text-gray-600 dark:text-warm-sand" />
                        </button>
                      )}
                      <div className="relative">
                        <button
                          className="p-2 hover:bg-gray-100 dark:hover:bg-charcoal-700 rounded-lg transition-colors"
                          onClick={() => setActiveMenu(activeMenu === product.id ? null : product.id)}
                        >
                          <MoreVertical className="w-4 h-4 text-gray-600 dark:text-warm-sand" />
                        </button>
                        {activeMenu === product.id && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setActiveMenu(null)} />
                            <div className="absolute right-0 bottom-full mb-1 w-40 bg-white dark:bg-charcoal-800 border border-gray-200 dark:border-charcoal-700 rounded-lg shadow-lg z-20 py-1">
                              <button
                                onClick={() => handleEdit(product)}
                                className="w-full px-4 py-2 text-left text-sm text-gray-900 dark:text-warm-cream hover:bg-gray-100 dark:hover:bg-charcoal-700 flex items-center gap-2"
                              >
                                <span>Edit</span>
                              </button>
                              <button
                                onClick={() => handleDuplicate(product)}
                                className="w-full px-4 py-2 text-left text-sm text-gray-900 dark:text-warm-cream hover:bg-gray-100 dark:hover:bg-charcoal-700 flex items-center gap-2"
                              >
                                <Copy className="w-4 h-4" />
                                <span>Duplicate</span>
                              </button>
                              <button
                                onClick={() => handleDelete(product)}
                                className="w-full px-4 py-2 text-left text-sm text-danger-600 dark:text-danger-400 hover:bg-danger-500/10 flex items-center gap-2"
                              >
                                <Trash2 className="w-4 h-4" />
                                <span>Delete</span>
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <ProductFormShell
        isOpen={showAddModal}
        onClose={handleCloseModal}
        onSuccess={handleSuccess}
        product={editingProduct || duplicateProduct}
        mode={modalMode}
        draftId={activeDraftId}
        onDraftIdChange={setActiveDraftId}
      />
    </div>
  );
}
